import {
  DESKTOP_PROTOCOL_VERSION,
  DesktopBridgeErrorSchema,
  DesktopCapabilityActivationDataSchema,
  DesktopCapabilityActivationResultSchema,
  DesktopCapabilityKindSchema,
  DesktopCapabilityRequestMessageSchema,
  DesktopCapabilityResponseMessageSchema,
  DesktopClientRequestIdSchema,
  DesktopCloseResponseMessageSchema,
  DesktopCloseResultSchema,
  DesktopFileGrantIdSchema as DesktopFileHandleSchema,
  DesktopFrameMessageSchema,
  DesktopLocalSessionHandleSchema,
  DesktopLogicalBindingSchema,
  DesktopLoopbackEndpointSchema,
  DesktopLoopbackHeaderSchema as DesktopLoopbackRequestHeaderSchema,
  DesktopLoopbackRequestIdSchema as DesktopLoopbackRequestHandleSchema,
  DesktopLoopbackRequestRecordSchema,
  DesktopOperationDataSchema,
  DesktopOperationNameSchema,
  DesktopOperationRequestMessageSchema,
  DesktopOperationResponseMessageSchema,
  DesktopOperationResultSchema,
  DesktopProtocolVersionSchema,
  DesktopSessionCloseMessageSchema,
  DesktopSessionEventMessageSchema,
  DesktopTcpConnectionIdSchema as DesktopTcpConnectionHandleSchema,
  SortedDesktopOperationsSchema,
  desktopSemanticOperationFor,
  operationsMatchCapability,
  type DesktopCapabilityActivationResult,
  type DesktopCloseResult,
  type DesktopFrameMessage,
  type DesktopOperationRequest,
  type DesktopOperationResult,
} from "@ccbox/desktop-app-sdk/protocol";
import { z } from "zod";

export {
  DESKTOP_PROTOCOL_VERSION,
  DesktopBridgeErrorSchema,
  DesktopCapabilityActivationDataSchema,
  DesktopCapabilityActivationResultSchema,
  DesktopCapabilityKindSchema,
  DesktopCapabilityRequestMessageSchema,
  DesktopCapabilityResponseMessageSchema,
  DesktopClientRequestIdSchema,
  DesktopCloseResponseMessageSchema,
  DesktopCloseResultSchema,
  DesktopFileHandleSchema,
  DesktopFrameMessageSchema,
  DesktopLocalSessionHandleSchema,
  DesktopLogicalBindingSchema,
  DesktopLoopbackEndpointSchema,
  DesktopLoopbackRequestHandleSchema,
  DesktopLoopbackRequestHeaderSchema,
  DesktopLoopbackRequestRecordSchema,
  DesktopOperationDataSchema,
  DesktopOperationNameSchema,
  DesktopOperationRequestMessageSchema,
  DesktopOperationResponseMessageSchema,
  DesktopOperationResultSchema,
  DesktopProtocolVersionSchema,
  DesktopSessionCloseMessageSchema,
  DesktopSessionEventMessageSchema,
  DesktopTcpConnectionHandleSchema,
  SortedDesktopOperationsSchema,
  operationsMatchCapability,
};
export type {
  DesktopCapabilityActivationResult,
  DesktopCloseResult,
  DesktopFrameMessage,
  DesktopOperationRequest,
  DesktopOperationResult,
};

const DesktopOpaqueIdSchema = z
  .string()
  .min(5)
  .max(128)
  .regex(/^[a-z][a-z0-9_]*$/u, "Expected an opaque platform ID");

export const DESKTOP_CHALLENGE_DOMAIN = "ccbox.desktop.capability-challenge.v1" as const;
export const DESKTOP_REDEEM_DOMAIN = "ccbox.desktop.establishment-redeem.v1" as const;
export const DESKTOP_HEARTBEAT_DOMAIN = "ccbox.desktop.capability-heartbeat.v1" as const;
export const DESKTOP_CLOSE_DOMAIN = "ccbox.desktop.capability-close.v1" as const;

export const DesktopTokenSchema = z
  .string()
  .length(43)
  .regex(/^[A-Za-z0-9_-]+$/u);
export const DesktopSignatureSchema = z
  .string()
  .length(86)
  .regex(/^[A-Za-z0-9_-]+$/u);
export const DesktopPublicKeySpkiSchema = z
  .string()
  .min(80)
  .max(256)
  .regex(/^[A-Za-z0-9_-]+$/u);

export const DesktopSupportedCapabilitiesSchema = z
  .array(DesktopCapabilityKindSchema)
  .min(1)
  .max(DesktopCapabilityKindSchema.options.length)
  .superRefine((capabilities, context) => {
    if (new Set(capabilities).size !== capabilities.length) {
      context.addIssue({
        code: "custom",
        message: "Supported capabilities must be unique",
      });
    }
    const sorted = [...capabilities].sort();
    if (capabilities.some((capability, index) => capability !== sorted[index])) {
      context.addIssue({
        code: "custom",
        message: "Supported capabilities must be lexically sorted",
      });
    }
  });

export const DesktopCapabilityChallengeRequestSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  appInstanceId: DesktopOpaqueIdSchema,
  deviceId: DesktopOpaqueIdSchema,
  deviceDisplayName: z.string().trim().min(1).max(80),
  platform: z.enum(["darwin", "win32", "linux"]),
  architecture: z.enum(["arm64", "x64"]),
  keyVersion: z.literal(1),
  publicKeySpki: DesktopPublicKeySpkiSchema,
  binding: DesktopLogicalBindingSchema,
  operations: SortedDesktopOperationsSchema,
});

export const DesktopCapabilityChallengeCreatedSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  challengeId: DesktopTokenSchema,
  expiresAt: z.number().int().positive(),
});

export const DesktopCanonicalChallengeSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  challengeId: DesktopTokenSchema,
  nonce: DesktopTokenSchema,
  deviceId: DesktopOpaqueIdSchema,
  shellSessionId: DesktopOpaqueIdSchema,
  userId: DesktopOpaqueIdSchema,
  organizationId: DesktopOpaqueIdSchema,
  appInstanceId: DesktopOpaqueIdSchema,
  binding: DesktopLogicalBindingSchema,
  profileId: DesktopOpaqueIdSchema,
  operations: SortedDesktopOperationsSchema,
  issuedAt: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
});

export type DesktopCanonicalChallenge = z.infer<typeof DesktopCanonicalChallengeSchema>;

export const DesktopChallengeDocumentSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  challenge: DesktopCanonicalChallengeSchema,
  consent: z.strictObject({
    organizationName: z.string().min(1).max(160),
    appDisplayName: z.string().min(1).max(120),
    profileDisplayName: z.string().min(1).max(120),
    capability: DesktopCapabilityKindSchema,
    maximumSessionSeconds: z.number().int().min(1).max(28_800),
    offlineGraceSeconds: z.number().int().min(0).max(300),
  }),
});

export function canonicalDesktopChallenge(rawChallenge: DesktopCanonicalChallenge): string {
  const challenge = DesktopCanonicalChallengeSchema.parse(rawChallenge);
  return JSON.stringify([
    DESKTOP_CHALLENGE_DOMAIN,
    challenge.challengeId,
    challenge.nonce,
    challenge.deviceId,
    challenge.shellSessionId,
    challenge.userId,
    challenge.organizationId,
    challenge.appInstanceId,
    challenge.binding,
    challenge.profileId,
    challenge.operations,
    challenge.issuedAt,
    challenge.expiresAt,
  ]);
}

export const DesktopDeviceAssertionRequestSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  deviceId: DesktopOpaqueIdSchema,
  signature: DesktopSignatureSchema,
});

export const DesktopEstablishmentGrantCreatedSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  establishmentGrant: DesktopTokenSchema,
  deviceId: DesktopOpaqueIdSchema,
  expiresAt: z.number().int().positive(),
});

const DesktopSignedDeviceRequestBaseSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  deviceId: DesktopOpaqueIdSchema,
  requestNonce: DesktopTokenSchema,
  signedAt: z.number().int().positive(),
  signature: DesktopSignatureSchema,
});

export const DesktopEstablishmentRedeemRequestSchema = DesktopSignedDeviceRequestBaseSchema.extend({
  establishmentGrant: DesktopTokenSchema,
});

export function canonicalDesktopEstablishmentRedeem(input: {
  establishmentGrant: string;
  deviceId: string;
  requestNonce: string;
  signedAt: number;
}): string {
  return JSON.stringify([
    DESKTOP_REDEEM_DOMAIN,
    input.establishmentGrant,
    input.deviceId,
    input.requestNonce,
    input.signedAt,
  ]);
}

export const DesktopCapabilitySessionCreatedSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  capabilitySessionId: DesktopOpaqueIdSchema,
  deviceId: DesktopOpaqueIdSchema,
  binding: DesktopLogicalBindingSchema,
  capability: DesktopCapabilityKindSchema,
  allowedOperations: SortedDesktopOperationsSchema,
  expiresAt: z.number().int().positive(),
  heartbeatIntervalSeconds: z.literal(15),
  requestDeadlineSeconds: z.literal(5),
  offlineGraceSeconds: z.number().int().min(0).max(300),
});

export const DesktopHeartbeatRequestSchema = DesktopSignedDeviceRequestBaseSchema.extend({
  sequence: z.number().int().positive(),
});

export function canonicalDesktopHeartbeat(
  capabilitySessionId: string,
  input: z.infer<typeof DesktopHeartbeatRequestSchema>,
): string {
  return JSON.stringify([
    DESKTOP_HEARTBEAT_DOMAIN,
    capabilitySessionId,
    input.deviceId,
    input.sequence,
    input.requestNonce,
    input.signedAt,
  ]);
}

export const DesktopHeartbeatResponseSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  authorized: z.literal(true),
  expiresAt: z.number().int().positive(),
  nextHeartbeatInSeconds: z.literal(15),
});

export const DesktopCloseRequestSchema = DesktopSignedDeviceRequestBaseSchema.extend({
  sequence: z.number().int().positive(),
});

export function canonicalDesktopClose(
  capabilitySessionId: string,
  input: z.infer<typeof DesktopCloseRequestSchema>,
): string {
  return JSON.stringify([
    DESKTOP_CLOSE_DOMAIN,
    capabilitySessionId,
    input.deviceId,
    input.sequence,
    input.requestNonce,
    input.signedAt,
  ]);
}

export function desktopAuthorizationOperationName(
  operation: DesktopOperationRequest,
): z.infer<typeof DesktopOperationNameSchema> {
  return desktopSemanticOperationFor(operation);
}

export const DesktopAvailabilityDataSchema = z.discriminatedUnion("available", [
  z.strictObject({
    available: z.literal(true),
    deviceId: DesktopOpaqueIdSchema,
    deviceDisplayName: z.string().trim().min(1).max(80),
    platform: z.enum(["darwin", "win32", "linux"]),
    architecture: z.enum(["arm64", "x64"]),
    keyVersion: z.literal(1),
    publicKeySpki: DesktopPublicKeySpkiSchema,
    supportedCapabilities: DesktopSupportedCapabilitiesSchema,
  }),
  z.strictObject({
    available: z.literal(false),
    reason: z.enum(["browser", "device_unavailable", "key_unavailable", "unsupported_version"]),
  }),
]);

export const DesktopAvailabilityResultSchema = z.discriminatedUnion("ok", [
  z.strictObject({
    protocolVersion: DesktopProtocolVersionSchema,
    ok: z.literal(true),
    data: DesktopAvailabilityDataSchema,
  }),
  z.strictObject({
    protocolVersion: DesktopProtocolVersionSchema,
    ok: z.literal(false),
    error: DesktopBridgeErrorSchema,
  }),
]);

export const DesktopBridgeSessionEventSchema = z.strictObject({
  protocolVersion: DesktopProtocolVersionSchema,
  sessionHandle: DesktopLocalSessionHandleSchema,
  type: z.enum(["stateChanged", "dataAvailable", "resourceDisconnected"]),
  state: z.enum(["active", "grace", "revoked", "expired", "closed"]).optional(),
  occurredAt: z.number().int().positive(),
});

export type DesktopCapabilityChallengeRequest = z.infer<
  typeof DesktopCapabilityChallengeRequestSchema
>;
